import { showNotification } from '@helpers/notification-helper';
import { getShortDateFormat } from '@helpers/utils-helpers';
import { useAppDispatch } from '@hooks/app/redux';
import useOperationStatus from '@lib/hooks/useOperationStatus';
import { actions as eVisitsActions } from '@store/ducks/e-visits';
import { Drawer } from '@ui';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isRangeError } from '../../../../../type-guards/e-visits';
import { IOfficeHoursWeekRuleWithId, TRulesManipulationError } from 'types/e-visits';
import OfficeHoursEdit from '../office-hours-edit';
import { IFormValues } from '../office-hours-edit/office-hours-edit.schema';

interface OfficeHoursDrawerProps {
  visible: boolean;
  onClose: () => void;
  afterSubmit: () => void;

  expertiseCode: string;
  medicalInstitutionId: string;
  officeHours?: IOfficeHoursWeekRuleWithId;
}

const OfficeHoursDrawer: React.FunctionComponent<OfficeHoursDrawerProps> = ({
  expertiseCode,
  medicalInstitutionId,
  afterSubmit,
  onClose,
  visible,
  officeHours,
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const handleSubmit = React.useCallback(
    (values: IFormValues) => {
      if (values.id) {
        dispatch(
          eVisitsActions.updateOfficeRuleRequest({
            id: values.id!,
            ...values,
          }),
        );
      } else {
        dispatch(
          eVisitsActions.createOfficeRuleRequest({
            ...values,
          }),
        );
      }
    },
    [dispatch],
  );

  const onSuccess = React.useCallback(() => {
    showNotification({
      type: 'SUCCESS',
      title: formatMessage({ id: 'eVisit_rules_success' }),
      copy: '',
    });
    afterSubmit();
  }, [formatMessage, afterSubmit]);

  const onError = React.useCallback(
    (error: TRulesManipulationError) => {
      if (isRangeError(error)) {
        showNotification({
          type: 'ERROR',
          title: formatMessage({ id: `eVisit_rules_error_${error.errCode}` }),
          copy: formatMessage(
            { id: 'eVisit_rules_error_range_error' },
            {
              windows: error.detail.conflicts
                .map((d) => moment(d.fromDate).format(getShortDateFormat()))
                .join(', '),
            },
          ),
        });
      } else {
        console.log('ERR', error);
        showNotification({
          type: 'ERROR',
          title: formatMessage({ id: `eVisit_rules_error_${error.errCode}` }),
          copy: '',
        });
      }
    },
    [formatMessage],
  );

  const { status: createStatus } = useOperationStatus({
    stateGetter: (state) => state.eVisits.createOfficeRuleStatus,
    errorGetter: (state) => state.eVisits.createOfficeRuleError,
    resetAction: eVisitsActions.createOfficeRuleReset,
    onSuccess,
    onError,
  });

  const { status: updateStatus } = useOperationStatus({
    stateGetter: (state) => state.eVisits.updateOfficeRuleStatus,
    errorGetter: (state) => state.eVisits.updateOfficeRuleError,
    resetAction: eVisitsActions.updateOfficeRuleReset,
    onSuccess,
    onError,
  });

  const loading = createStatus === 'pending' || updateStatus === 'pending';

  return (
    <Drawer
      visible={visible}
      title={<FormattedMessage id="office_hours_title" />}
      onClose={onClose}
      destroyOnClose
    >
      <OfficeHoursEdit
        officeHours={officeHours}
        expertiseCode={expertiseCode}
        medicalInstitutionId={medicalInstitutionId}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </Drawer>
  );
};

export default OfficeHoursDrawer;
